import type { RenderContext, VideoFrame } from '../../types';
import { BaseEffect } from '../base';
import { withOverlay } from '../environmental/composite';
import { hash01, particleSeed } from '../../utils/deterministic';

const DEFAULTS = { density: 0.55, size: 0.7, twinkle: 0.8, brightness: 1 };

export class SparklesEffect extends BaseEffect {
  constructor(id: string, clip: Partial<import('../../types').EffectTimelineClip>) {
    super(id, 'sparkles', clip, DEFAULTS);
  }

  protected renderGpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    return this.renderCpu(frame, params, context);
  }

  protected renderCpu(frame: VideoFrame, params: Record<string, number>, context: RenderContext): VideoFrame {
    const seed = particleSeed('sparkles', 0, context.seed || 42);
    const count = Math.min(180, Math.round(40 + params.density * 140));
    const t = context.frameIndex * 0.08;
    return withOverlay(frame, (ctx, w, h) => {
      for (let i = 0; i < count; i += 1) {
        const x = hash01(i, 0, seed) * w;
        const y = hash01(i, 1, seed) * h;
        const phase = hash01(i, 2, seed) * Math.PI * 2;
        const tw = 0.35 + 0.65 * Math.max(0, Math.sin(t * (1 + params.twinkle) + phase));
        const r = (1.2 + hash01(i, 3, seed) * 4.5) * params.size * tw;
        const a = tw * params.brightness * (0.35 + hash01(i, 4, seed) * 0.55);

        const g = ctx.createRadialGradient(x, y, 0, x, y, r * 2.8);
        g.addColorStop(0, `rgba(255,255,245,${Math.min(1, a)})`);
        g.addColorStop(0.25, `rgba(255,240,200,${a * 0.55})`);
        g.addColorStop(1, 'rgba(255,220,160,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, r * 2.8, 0, Math.PI * 2);
        ctx.fill();

        // Cross sparkle
        ctx.strokeStyle = `rgba(255,255,255,${a * 0.85})`;
        ctx.lineWidth = Math.max(0.6, r * 0.25);
        ctx.beginPath();
        ctx.moveTo(x - r * 1.8, y);
        ctx.lineTo(x + r * 1.8, y);
        ctx.moveTo(x, y - r * 1.8);
        ctx.lineTo(x, y + r * 1.8);
        ctx.stroke();
      }
    }, 'lighter');
  }
}
